import React, { useState } from 'react';
import { 
  ThumbsUp, ThumbsDown, MessageSquare, CheckCircle, 
  MoreVertical, Edit, Trash2, Flag, User, Calendar 
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import AnswerForm from './AnswerForm';
import MarkdownRenderer from '../MarkdownRenderer';

const AnswerItem = ({ 
  answer, 
  onVote, 
  onAccept, 
  onDelete, 
  onReply,
  onEdit,
  replies = [],
  canAcceptAnswer = false,
  isAccepted = false,
  userId,
  userRole,
  className = "border-gray-200 dark:border-gray-700"
}) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const isOwner = userId === answer.userId;
  const isModOrAdmin = userRole === 'admin' || userRole === 'moderator';
  
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const handleReplySubmit = async (content) => {
    await onReply(content);
    setShowReplyForm(false);
    
    // Auto-show replies after submitting
    setShowReplies(true);
  };
  
  const handleReport = () => {
    toast.error('Report functionality coming soon');
    setShowDropdown(false);
  };

  const handleEditSubmit = async (content) => {
    if (onEdit) {
      await onEdit(content);
      setIsEditing(false);
    }
    setShowDropdown(false);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setShowDropdown(false);
  };
  
  return (
    <div className={`border ${className} rounded-lg p-4 bg-white dark:bg-gray-800`}>
      <div className="flex">
        {/* Vote column */}
        <div className="flex flex-col items-center space-y-2 mr-4">
          <button
            onClick={() => onVote('upvote')}
            className={`p-1 rounded-full ${
              answer.userVote === 'upvote'
                ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400'
            }`}
          >
            <ThumbsUp size={18} />
          </button>
          
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {answer.upvotes - answer.downvotes}
          </span>
          
          <button
            onClick={() => onVote('downvote')}
            className={`p-1 rounded-full ${
              answer.userVote === 'downvote'
                ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400'
            }`}
          >
            <ThumbsDown size={18} />
          </button>
          
          {canAcceptAnswer && (
            <button
              onClick={onAccept}
              className={`mt-2 p-1 rounded-full ${
                isAccepted
                  ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                  : 'text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400'
              }`}
              title={isAccepted ? 'Accepted answer' : 'Accept as answer'}
            >
              <CheckCircle size={20} />
            </button>
          )}
        </div>
        
        {/* Answer content */}
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-3">
              <User size={14} className="mr-1" />
              <Link 
                to={`/users/${answer.userId}`}
                className="mr-3 hover:text-red-600 dark:hover:text-red-400"
              >
                {answer.userName}
              </Link>
              <Calendar size={14} className="mr-1" />
              <span>{formatDate(answer.createdAt)}</span>
            </div>
            
            {/* Dropdown menu */}
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-full focus:outline-none"
              >
                <MoreVertical size={16} />
              </button>
              
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-[9999] border border-gray-200 dark:border-gray-700">
                  <ul className="py-1">
                    {(isOwner || isModOrAdmin) && (
                      <>
                        <li>
                          <button
                            onClick={() => {
                              setIsEditing(true);
                              setShowDropdown(false);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Edit size={16} className="mr-2" />
                            Edit
                          </button>
                        </li>
                        <li>
                          <button
                            onClick={() => {
                              onDelete();
                              setShowDropdown(false);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Trash2 size={16} className="mr-2" />
                            Delete
                          </button>
                        </li>
                      </>
                    )}
                    
                    {!isOwner && (
                      <li>
                        <button
                          onClick={handleReport}
                          className="flex items-center w-full px-4 py-2 text-sm text-yellow-600 dark:text-yellow-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <Flag size={16} className="mr-2" />
                          Report
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
          
          <div className="mb-4">
            {isEditing ? (
              <AnswerForm 
                initialContent={answer.content}
                onSubmit={handleEditSubmit}
                onCancel={handleEditCancel}
                placeholder="Edit your answer..."
              />
            ) : (
              <MarkdownRenderer content={answer.content} />
            )}
          </div>
          
          <div className="flex items-center mt-4 space-x-4 text-sm">
            <button
              onClick={() => setShowReplyForm(!showReplyForm)}
              className="flex items-center text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
            >
              <MessageSquare size={16} className="mr-1" />
              Reply
            </button>
            
            {answer.replyCount > 0 && (
              <button
                onClick={() => setShowReplies(!showReplies)}
                className="flex items-center text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
              >
                {showReplies ? 'Hide replies' : `Show ${answer.replyCount} ${answer.replyCount === 1 ? 'reply' : 'replies'}`}
              </button>
            )}
          </div>
          
            {/* Reply form */}
            {showReplyForm && (
            <div className="mt-4 ml-0 md:ml-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800">
                <AnswerForm 
                onSubmit={handleReplySubmit} 
                onCancel={() => setShowReplyForm(false)}
                placeholder="Write your reply..."
                isReply={true}
                />
            </div>
            )}

            {/* Replies */}
            {showReplies && replies.length > 0 && (
              <div className="mt-4 ml-0 md:ml-6 space-y-4">
                {replies.map(reply => (
                  <div key={reply.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-2">
                      <User size={14} className="mr-1" />
                      <Link 
                        to={`/users/${reply.userId}`}
                        className="mr-3 hover:text-red-600 dark:hover:text-red-400"
                      >
                        {reply.userName}
                      </Link>
                      <Calendar size={14} className="mr-1" />
                      <span>{formatDate(reply.createdAt)}</span>
                    </div>
                    
                    <MarkdownRenderer content={reply.content} />
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default AnswerItem;